import { startTransition, use, useDeferredValue, useMemo } from "react";

import { useFragmentAnalytics } from "lib/analytics/use-fragment-analytics";
import { useFullBookAnalytics } from "lib/analytics/use-full-book-analytics";
import { ContentContext } from "lib/content/use-content";
import { useHighlights } from "lib/highlights/use-highlights";
import { buildVirtualLayout, measureLayout } from "lib/layout/capture-layout";
import {
  BUFFER_SIZE,
  ContentLayoutContext,
} from "lib/layout/use-content-layout";
import { useContentStyles } from "lib/layout/use-content-styles";
import { useVirtualLayout } from "lib/layout/use-virtual-layout";
import { useQuotes } from "lib/quotes/use-quotes";
import { SettingsContext } from "lib/settings/use-settings";
import { useStats } from "lib/stats/use-stats";
import { useToc } from "lib/toc/use-toc";
import { Loader } from "lib/ui/loader";

import { FullContent } from "app/full-content";
import { ContentPart } from "./content-part";
import css from "./content.module.scss";

export function Content() {
  const { layout, isPending, layoutKey } = use(ContentLayoutContext)!;
  const { initialContentParts } = use(ContentContext)!;
  const { toc } = useToc();
  const totalParts = toc?.Parts?.length ?? 0;
  const { root } = useContentStyles();

  return (
    <div id="content" className={css.root} style={root}>
      <VisibleContent />
      {layout && (isPending || initialContentParts.length < totalParts) && (
        <FullContent key={layoutKey} />
      )}
      {isPending && (
        <div className={css.loadingOverlay}>
          <Loader showProgress={false} />
        </div>
      )}
    </div>
  );
}

function VisibleContent() {
  useStats();
  useFragmentAnalytics();
  useFullBookAnalytics();
  const { content, currentPartIndex, initialContentParts, initialPartIndex } =
    use(ContentContext)!;
  const { setLayout, layout } = use(ContentLayoutContext)!;
  const { cols } = use(SettingsContext)!;
  const { toc } = useToc();
  const { content: contentStyles } = useContentStyles();
  const virtualLayout = useVirtualLayout();
  const skipTranslation = toc?.Meta?.Lang === document.documentElement.lang;

  // Хайлайты цитат — один раз на весь видимый контент, а не на каждую часть
  const { quotes } = useQuotes();
  const deferredQuotes = useDeferredValue(quotes);
  useHighlights(deferredQuotes);

  const totalParts = toc?.Parts?.length ?? 0;
  const hasFullContent =
    content.length === totalParts &&
    content.filter(Boolean).length === totalParts;

  const { parts, firstVisiblePartIndex } = useMemo(() => {
    if (hasFullContent) {
      const partIndex = currentPartIndex;
      const firstIndex = Math.max(0, partIndex - BUFFER_SIZE);
      const lastIndex = Math.min(totalParts - 1, partIndex + BUFFER_SIZE);
      const sliced = content.slice(firstIndex, lastIndex + 1);
      return {
        parts: sliced,
        firstVisiblePartIndex: firstIndex,
      };
    } else {
      const firstIndex = Math.max(0, initialPartIndex - BUFFER_SIZE);
      return {
        parts: initialContentParts,
        firstVisiblePartIndex: firstIndex,
      };
    }
  }, [
    hasFullContent,
    content,
    initialContentParts,
    currentPartIndex,
    initialPartIndex,
    totalParts,
  ]);

  const visibleParts = useMemo(() => {
    return parts.map((partContent, arrayIndex) => ({
      index: firstVisiblePartIndex + arrayIndex,
      content: partContent,
    }));
  }, [parts, firstVisiblePartIndex]);

  function refCallback(node: HTMLDivElement | null) {
    if (!node || !toc || parts.length === 0 || layout?.mode === "virtual")
      return;
    startTransition(() => {
      setLayout(() => {
        const measured = measureLayout(node, cols);
        if (!measured) return null;
        return buildVirtualLayout(measured, parts, toc, firstVisiblePartIndex);
      });
    });
  }

  return (
    <div
      ref={refCallback}
      data-testid={layout?.mode === "basic" ? "basicContent" : "virtualContent"}
      id="virtual-content"
      translate={skipTranslation ? "no" : undefined}
      className={css.virtualContent}
      style={{ ...contentStyles, ...virtualLayout }}
    >
      {visibleParts.map(({ index, content }) => (
        <ContentPart key={`part-${index}`} part={content} partIndex={index} />
      ))}
    </div>
  );
}
